import React from 'react';
import { Badge } from 'reactstrap';
import { Components } from 'react-markdown';
import MarkdownViewer from '../MarkdownViewer';
import replaceCitationPlaceholders from './replaceCitationPlaceholders';
import {
    CitationInfo,
    MessageItemDto,
    OutputTextContentPartDto,
    RefusalContentPartDto,
    ImageFileContentPartDto,
    ResponsesMessageRecord
} from './responses-types';

interface MessageBubbleProps {
    record: ResponsesMessageRecord;
    citations: CitationInfo[];
    /** Called when a citation badge is clicked — scrolls the sidebar card into view. */
    onCitationClick: (fileId: string) => void;
    /**
     * This message's run file_search results, indexed [turn][file], used to
     * resolve OpenAI private-use citation markers (fileciteturnNfileM).
     */
    turns?: string[][];
}

/**
 * Renders a single message bubble from a ResponsesMessageRecord.
 *
 * - user_message / assistant_message → text bubble (left/right)
 * - file_search_call (in_progress) → "Searching…" indicator (no bubble when done)
 * - All other types → not rendered (placeholder: logged in console)
 * - Unknown types → not rendered
 */
const MessageBubble: React.FC<MessageBubbleProps> = ({
    record,
    citations,
    onCitationClick,
    turns
}) => {
    const { messageType, message } = record;

    if (
        messageType !== 'user_message' &&
        messageType !== 'assistant_message'
    ) {
        if (messageType === 'file_search_call') {
            const status = (message as { status?: string } | null)?.status;
            if (status === 'in_progress') {
                return (
                    <div className='message-bubble bg-light text-dark rounded'>
                        <small className='text-muted fst-italic'>
                            Searching knowledge base…
                        </small>
                    </div>
                );
            }
            // Completed file_search_call — no bubble (citations appear in sidebar)
            return null;
        }

        // Placeholder for unsupported types (reasoning, code_interpreter, etc.)
        console.debug(`[MessageBubble] Unsupported messageType "${messageType}" — not rendered`);
        return null;
    }

    const isUser = messageType === 'user_message';
    const messageItem = message as MessageItemDto | null;

    return (
        <div
            className={`message-bubble ${
                isUser ? 'bg-primary-hf text-white' : 'bg-light text-dark'
            } rounded`}
        >
            {(messageItem?.content ?? []).map((part, idx) => {
                if (part.type === 'output_text') {
                    const outputPart = part as OutputTextContentPartDto;
                    return (
                        <MarkdownViewer
                            key={idx}
                            content={replaceCitationPlaceholders(outputPart, citations, turns)}
                            components={buildMarkdownComponents(citations, onCitationClick)}
                        />
                    );
                }

                if (part.type === 'input_text') {
                    // User messages — render plain text via markdown
                    return (
                        <MarkdownViewer
                            key={idx}
                            content={part.text}
                        />
                    );
                }

                if (part.type === 'refusal') {
                    return (
                        <small key={idx} className='text-danger'>
                            {(part as RefusalContentPartDto).refusal}
                        </small>
                    );
                }

                if (part.type === 'image_file') {
                    const imgPart = part as ImageFileContentPartDto;
                    return (
                        <small key={idx} className='text-muted fst-italic'>
                            [Image: {imgPart.image_file.file_id}]
                        </small>
                    );
                }

                return null;
            })}
        </div>
    );
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function buildMarkdownComponents(
    citations: CitationInfo[],
    onCitationClick: (fileId: string) => void
): Partial<Components> {
    return {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        a: ({ href, children, node, title, ...props }) => {
            const isCitation = href?.startsWith('#citation-');

            if (isCitation) {
                const fileId = href!.replace('#citation-', '');
                const indexOfCitation = citations.findIndex(
                    (c) => c.fileId === fileId
                );
                const citation = citations[indexOfCitation];

                return (
                    <a
                        href='#'
                        onClick={(e) => {
                            e.preventDefault();
                            onCitationClick(fileId);
                        }}
                        title={citation?.title}
                        style={{ textDecoration: 'none', cursor: 'pointer' }}
                        {...props}
                    >
                        <Badge
                            pill
                            color='primary'
                            className='align-text-top'
                            style={{
                                cursor: 'pointer',
                                fontSize: '0.75rem',
                                padding: '0.25em 0.6em',
                                marginLeft: '0.2em',
                                marginRight: '0.2em'
                            }}
                        >
                            {indexOfCitation + 1}
                        </Badge>
                    </a>
                );
            }

            const isExternal =
                href && !href.startsWith('/') && !href.startsWith('#');
            return (
                <a
                    href={href}
                    target={isExternal ? '_blank' : undefined}
                    rel={isExternal ? 'noopener noreferrer' : undefined}
                    {...props}
                >
                    {children}
                </a>
            );
        }
    };
}

export default MessageBubble;
